'use client'

import React from 'react'

import {PositioningPortal} from '@codastic/react-positioning-portal'
import find from 'lodash/find'
import {CSSTransition} from 'react-transition-group'

import useTimeout from '../../hooks/use-timeout'
import cn from '../../libs/class-names'

export type Position = 'top' | 'bottom' | 'left' | 'right'

const positionStrategy =
  (preferredPosition: Position) => (parentRect: ClientRect, portalRect: ClientRect) => {
    const scrollX = window.scrollX || window.pageXOffset
    const scrollY = window.scrollY || window.pageYOffset
    const body = window.document.documentElement || window.document.body

    const horizontalCenter = (parentRect.width - portalRect.width) / 2
    const verticalCenter = (parentRect.height - portalRect.height) / 2
    const additionalPadding = 8

    const positions = {
      bottom: {
        position: 'bottom',
        top: parentRect.top + parentRect.height + scrollY + additionalPadding,
        left: parentRect.left + scrollX + horizontalCenter,
        enoughSpace:
          parentRect.top + parentRect.height + portalRect.height + additionalPadding <
          body.clientHeight,
      },
      top: {
        position: 'top',
        top: parentRect.top - portalRect.height + scrollY - additionalPadding,
        left: parentRect.left + scrollX + horizontalCenter,
        enoughSpace: parentRect.top - portalRect.height - additionalPadding > 0,
      },
      left: {
        position: 'left',
        top: parentRect.top + scrollY + verticalCenter,
        left: parentRect.left + scrollX - portalRect.width - additionalPadding,
        enoughSpace: parentRect.left - portalRect.width - additionalPadding > 0,
      },
      right: {
        position: 'right',
        top: parentRect.top + scrollY + verticalCenter,
        left: parentRect.left + scrollX + parentRect.width + additionalPadding,
        enoughSpace:
          parentRect.left + parentRect.width + portalRect.width + additionalPadding <
          body.clientWidth,
      },
    }

    // Horizontal fallback preferred
    let sortedPositions = [
      positions[preferredPosition],
      positions.bottom,
      positions.top,
      positions.right,
      positions.left,
    ]

    // Vertical fallback preferred
    if (preferredPosition === 'left' || preferredPosition === 'right') {
      sortedPositions = [
        positions[preferredPosition],
        positions.right,
        positions.left,
        positions.bottom,
        positions.top,
      ]
    }

    const pickedPosition =
      find(sortedPositions, ({enoughSpace}) => enoughSpace) || positions[preferredPosition]

    const finalTop = Math.max(
      Math.min(pickedPosition.top, body.clientHeight + scrollY - portalRect.height),
      scrollY
    )
    const shiftY = finalTop - pickedPosition.top

    const finalLeft = Math.max(
      Math.min(pickedPosition.left, body.clientWidth + scrollX - portalRect.width),
      scrollX
    )
    const shiftX = finalLeft - pickedPosition.left

    return {
      top: Math.max(
        Math.min(pickedPosition.top, body.clientHeight + scrollY - portalRect.height),
        scrollY
      ),
      left: Math.max(
        Math.min(pickedPosition.left, body.clientWidth + scrollX - portalRect.width),
        scrollX
      ),
      strategy: {
        position: pickedPosition.position as Position,
        shift:
          pickedPosition.position === 'top' || pickedPosition.position === 'bottom'
            ? shiftX
            : shiftY,
      },
    }
  }

export interface Props {
  classNames?: string[]
  tooltip: React.ReactElement<{position?: Position; shift?: number}>
  children: React.ReactNode
  preferredPosition?: Position
  timeout?: number
  openOnHoverDelay?: number
  openOnHover?: boolean
}

const TooltipHandler = ({
  classNames,
  tooltip,
  children,
  preferredPosition = 'top',
  timeout = 5000,
  openOnHoverDelay = 0,
  openOnHover = false,
}: Props) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [isHovered, setIsHovered] = React.useState(false)
  /**
   * setTimeout is a closure, therefore, when setTimeout is scheduled it uses
   * the value of isHovered at that exact moment in time.
   * We have to use a ref to make sure the bool has the current value in time. */
  const isHoveredRef = React.useRef(isHovered)
  isHoveredRef.current = isHovered

  const open = () => setIsOpen(true)

  const close = () => setIsOpen(false)

  const onEnter = () => {
    setIsHovered(true)

    if (openOnHover) {
      if (openOnHoverDelay) {
        setTimeout(() => {
          if (isHoveredRef.current) {
            open()
          }
        }, openOnHoverDelay)
      } else {
        open()
      }
    }
  }

  const onTimeout = () => {
    if (!isHoveredRef.current) {
      close()
    }
  }

  const timedOut = useTimeout(timeout, isOpen && !!timeout, onTimeout)

  const onLeave = () => {
    setIsHovered(false)

    if (timedOut) {
      close()
    }
  }

  return (
    <PositioningPortal
      isOpen={isOpen}
      closeOnOutsideClick
      onShouldClose={close}
      positionStrategy={positionStrategy(preferredPosition)}
      portalContent={({strategy, transitionStarted, transitionEnded}) => (
        <CSSTransition
          classNames="TooltipHandler--transition"
          in={isOpen && !!strategy}
          onEnter={transitionStarted}
          onExited={transitionEnded}
          addEndListener={(node, done) => {
            node.addEventListener('transitioncancel', done, false)
            node.addEventListener('transitionend', done, false)
          }}
        >
          <div className="TooltipHandler__portal" onMouseEnter={onEnter} onMouseLeave={onLeave}>
            {React.cloneElement(tooltip, {position: strategy?.position, shift: strategy?.shift})}
          </div>
        </CSSTransition>
      )}
    >
      <span
        className={cn('TooltipHandler', {}, classNames)}
        onClick={open}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onBlur={close}
      >
        {children}
      </span>
    </PositioningPortal>
  )
}

export default TooltipHandler
